import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { Store, Loader2, CheckCircle2, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function VendorApply() {
  const navigate = useNavigate();
  const [f, setF] = useState({ business_name: "", description: "", categories: "", website: "" });
  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["my-vendor-app"],
    queryFn: () => api.get("/vendor/me/application").then((r) => r.data),
    retry: false,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.business_name.trim()) return toast.error("Please enter your business name.");
    setSubmitting(true);
    try {
      await api.post("/vendor/apply", {
        business_name: f.business_name, description: f.description, website: f.website,
        categories: f.categories.split(",").map((c) => c.trim()).filter(Boolean),
      });
      toast.success("Application submitted! We'll review it soon.");
      refetch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const set = (k, v) => setF({ ...f, [k]: v });
  const app = data?.application;

  const CATEGORY_OPTIONS = ["Musician", "Crafter", "Homesteader", "Artisan", "Herbalist", "Artist", "Author", "Wellness products", "Food & preserves"];
  const selectedCats = f.categories.split(",").map((c) => c.trim()).filter(Boolean);
  const toggleCat = (c) => {
    const has = selectedCats.some((x) => x.toLowerCase() === c.toLowerCase());
    const next = has ? selectedCats.filter((x) => x.toLowerCase() !== c.toLowerCase()) : [...selectedCats, c];
    set("categories", next.join(", "));
  };

  return (
    <div className="min-h-screen bg-[#F9F7F3] py-16 px-6" data-testid="vendor-apply-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 text-[#144F75] text-sm font-medium"><Store size={16} /> Become a Vendor</div>
        <h1 className="font-serif text-4xl text-[#1A242B] mt-2 mb-3">Sell your products in our marketplace</h1>
        <p className="text-[#4A5D6B] mb-8">Community Members can apply to become Vendors. Once approved, you'll get a seller dashboard to add products, list them in the marketplace, market them with rich content, and receive payouts.</p>
        <p className="text-sm text-[#8FA89B] -mt-6 mb-8">Offer healing, coaching or a practice too? <Link to="/enhancer/apply" className="text-[#9E5C7F] hover:underline">Apply as an Enhancer →</Link></p>

        {isLoading ? <div className="p-8 text-center text-[#8FA89B]"><Loader2 className="animate-spin inline" /></div>
          : data?.is_vendor ? (
            <div className="eyw-card p-8 text-center" data-testid="vendor-already">
              <CheckCircle2 className="mx-auto text-[#5C8A72]" size={40} />
              <h2 className="font-serif text-2xl mt-3">You're an approved Vendor 🎉</h2>
              <p className="text-[#4A5D6B] mt-2">Head to your seller dashboard to add products and set up payouts.</p>
              <button onClick={() => navigate("/enhancer")} data-testid="vendor-go-dashboard" className="eyw-btn mt-5">Go to Seller Dashboard</button>
            </div>
          ) : app?.status === "pending" ? (
            <div className="eyw-card p-8 text-center" data-testid="vendor-pending">
              <Clock className="mx-auto text-[#B5794E]" size={40} />
              <h2 className="font-serif text-2xl mt-3">Application under review</h2>
              <p className="text-[#4A5D6B] mt-2">Thanks! We received your application for <b>{app.business_name}</b>. We'll email you once it's reviewed.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="eyw-card p-7 space-y-4" data-testid="vendor-apply-form">
              {app?.status === "rejected" && <div className="text-sm text-[#B45151] bg-[#B45151]/10 rounded-lg px-3 py-2">Your previous application wasn't approved{app.reason ? `: ${app.reason}` : ""}. You're welcome to apply again.</div>}
              <div>
                <label className="text-sm font-medium text-[#1A242B] mb-1 block">Business / brand name *</label>
                <input value={f.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="vendor-business-name" className="eyw-input w-full" placeholder="e.g. Willow & Sage Botanicals" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#1A242B] mb-1 block">What do you sell?</label>
                <textarea rows={4} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="vendor-description" className="eyw-input w-full resize-none" placeholder="Tell us about your products and your story…" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#1A242B] mb-1 block">What do you make or sell?</label>
                <div className="flex flex-wrap gap-2 mb-2" data-testid="vendor-category-chips">
                  {CATEGORY_OPTIONS.map((c) => {
                    const active = selectedCats.some((x) => x.toLowerCase() === c.toLowerCase());
                    return (
                      <button type="button" key={c} onClick={() => toggleCat(c)} data-testid={`vendor-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${active ? "bg-[#9E5C7F] text-white border-[#9E5C7F]" : "bg-white text-[#4A5D6B] border-[#E5E0D8] hover:border-[#9E5C7F]"}`}>
                        {c}
                      </button>
                    );
                  })}
                </div>
                <input value={f.categories} onChange={(e) => set("categories", e.target.value)} data-testid="vendor-categories" className="eyw-input w-full" placeholder="Tap above or type your own, comma-separated" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#1A242B] mb-1 block">Website or social (optional)</label>
                <input value={f.website} onChange={(e) => set("website", e.target.value)} data-testid="vendor-website" className="eyw-input w-full" placeholder="https://" />
              </div>
              <button type="submit" disabled={submitting} data-testid="vendor-submit" className="eyw-btn w-full disabled:opacity-60">
                {submitting && <Loader2 className="animate-spin" size={16} />} Submit application
              </button>
            </form>
          )}
      </div>
    </div>
  );
}
